import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, Plus } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/States';
import { StatusPill } from '@/components/ui/StatusPill';
import { toast } from '@/components/ui/Toast';
import { runStateTone } from '@/components/runs/RunTranscript';
import { timeAgo } from '@/lib/timeAgo';
import { useIsStaff } from '@/app/store';
import { api, ApiError } from '@/lib/api';
import { useCompanyCtx } from './CompanyLayout';

/** Company index: the engagement table + "New workspace" for staff. */
export function CompanyOverviewPage() {
  const { company, reloadCompany } = useCompanyCtx();
  const isStaff = useIsStaff();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  return (
    <>
      {isStaff && (
        <div className="mb-4 flex justify-end">
          <Button onClick={() => setCreating((v) => !v)}>
            <Plus className="h-4 w-4" /> New workspace
          </Button>
        </div>
      )}

      {creating && (
        <NewWorkspaceForm companyId={company.id} companySlug={company.slug} onDone={() => { setCreating(false); reloadCompany(); }} />
      )}

      {company.workspaces.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="No workspaces yet"
          description={isStaff ? 'Create a workspace for this engagement.' : 'Nothing here yet.'}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          <div className="flex items-center border-b border-border px-4 py-2.5">
            <p className="font-semibold text-fg">Engagements</p>
          </div>
          <div className="hidden grid-cols-[2fr_110px_100px_110px_1.3fr_90px] items-center gap-4 border-b border-border px-4 py-2 lg:grid">
            {['Engagement', 'Status', 'Projects', 'Evidence', 'Last run', 'Pending'].map((h) => (
              <span key={h} className="text-xs font-medium text-fg-subtle">{h}</span>
            ))}
          </div>
          <ul className="divide-y divide-border">
            {company.workspaces.map((w) => (
              <li
                key={w.id}
                onClick={() => navigate(`/c/${company.slug}/w/${w.slug}`)}
                className="grid cursor-pointer grid-cols-1 items-center gap-2 px-4 py-3 transition hover:bg-surface-2/50 lg:grid-cols-[2fr_110px_100px_110px_1.3fr_90px] lg:gap-4"
              >
                <span className="truncate font-medium text-fg">{w.name}</span>
                <span onClick={(e) => e.stopPropagation()}>
                  <StatusPill
                    status={w.status}
                    busy={busyId === w.id}
                    onChange={isStaff ? (s) => setWorkspaceStatus(w.id, s) : undefined}
                  />
                </span>
                <span className="text-xs tabular-nums text-fg-muted">{w.projectCount}</span>
                <span className="text-xs tabular-nums text-fg-muted">
                  {w.contextCount} item{w.contextCount === 1 ? '' : 's'}
                </span>
                {w.lastRun ? (
                  <span className="flex items-center gap-2">
                    <Badge tone={runStateTone(w.lastRun.state)}>{w.lastRun.state.toLowerCase()}</Badge>
                    <span className="text-xs tabular-nums text-fg-subtle">{timeAgo(w.lastRun.at)}</span>
                  </span>
                ) : (
                  <span className="text-xs text-fg-subtle">no runs</span>
                )}
                <span>
                  {w.pendingSuggestions > 0 ? (
                    <span className="rounded-sm bg-accent px-1.5 py-0.5 text-2xs font-semibold text-white">
                      {w.pendingSuggestions}
                    </span>
                  ) : (
                    <span />
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );

  async function setWorkspaceStatus(workspaceId: string, status: string) {
    setBusyId(workspaceId);
    try {
      await api.updateWorkspace(workspaceId, { status: status as 'active' | 'paused' | 'archived' });
      reloadCompany();
    } catch (err) {
      toast.danger('Could not change status', err instanceof ApiError ? err.message : undefined);
    } finally {
      setBusyId(null);
    }
  }
}

function NewWorkspaceForm({
  companyId,
  companySlug,
  onDone,
}: {
  companyId: string;
  companySlug: string;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const ws = await api.createWorkspace(companyId, name.trim());
      toast.success('Workspace created');
      onDone();
      navigate(`/c/${companySlug}/w/${ws.slug}`);
    } catch (err) {
      toast.danger('Could not create workspace', err instanceof ApiError ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-6">
      <CardBody>
        <form className="flex flex-wrap items-end gap-3" onSubmit={submit}>
          <div className="min-w-64 flex-1">
            <Field label="Workspace name" htmlFor="ws-name">
              <Input id="ws-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. VIP Audits" required autoFocus />
            </Field>
          </div>
          <Button type="submit" disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create'}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
